import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  reload,
  User as FirebaseUser
} from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  documentId,
  writeBatch
} from 'firebase/firestore';
import { auth, db } from '@/services/firebase';
import { getPrivacyCompliantValue } from '@/config/privacy';
import { getClientIP } from './complianceApi';
import type { User, Organization, Form, Submission, RegisterRequest } from '@/types';

// Helper to wait for auth to be ready
let authReadyPromise: Promise<FirebaseUser | null> | null = null;

const waitForAuthReady = (): Promise<FirebaseUser | null> => {
  if (!authReadyPromise) {
    authReadyPromise = new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        unsubscribe();
        resolve(user);
      });
    });
  }
  return authReadyPromise;
};

// Simple helper to get current user or throw error
const getCurrentAuthUser = async () => {
  let user = auth.currentUser;
  
  // If no current user, wait for auth state to be determined
  if (!user) {
    user = await waitForAuthReady();
  }
  
  if (!user) {
    throw new Error('Not authenticated');
  }
  
  return user;
};

// Helper to get user's organization ID
const getUserOrganizationId = async (userId: string): Promise<string> => {
  try {
    // Get user document using UID as document ID (matching our new pattern)
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (!userDoc.exists()) {
      console.warn('[getUserOrganizationId] User profile not found, using userId as organizationId');
      return userId; // Fallback to using userId as organizationId
    }
    
    const userData = userDoc.data();
    const organizationId = userData.organizationId;
    
    if (!organizationId) {
      console.warn('[getUserOrganizationId] No organizationId in user profile, using userId as organizationId');
      return userId; // Fallback to using userId as organizationId
    }
    
    return organizationId;
  } catch (error) {
    console.error('[getUserOrganizationId] Error getting organization ID:', error);
    return userId; // Fallback to using userId as organizationId
  }
};

// Auth Functions
export const firebaseAuth = {
  async login(email: string, password: string) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      // Get user document from Firestore using UID as document ID
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      
      if (!userDoc.exists()) {
        throw new Error('User profile not found');
      }
      
      const userData = userDoc.data();
      
      // Get organization data
      const orgDoc = await getDoc(doc(db, 'organizations', userData.organizationId));
      if (!orgDoc.exists()) {
        throw new Error('Organization not found');
      }
      
      const orgData = orgDoc.data();
      
      return {
        user: {
          id: firebaseUser.uid,
          email: firebaseUser.email || '',
          organizationId: userData.organizationId,
          role: userData.role,
          createdAt: userData.createdAt?.toDate() || new Date(),
        } as User,
        organization: {
          id: userData.organizationId,
          name: orgData.name,
          email: orgData.email,
          facilityType: orgData.facilityType,
          subscription: orgData.subscription,
          createdAt: orgData.createdAt?.toDate() || new Date(),
        } as Organization,
        token: await firebaseUser.getIdToken(),
      };
    } catch (error: any) {
      throw new Error(error.message || 'Login failed');
    }
  },

  async register(data: RegisterRequest) {
    let userCredential: any = null;
    try {
      userCredential = await createUserWithEmailAndPassword(auth, data.email, data.password);
      const firebaseUser = userCredential.user;

      // Update user profile
      await updateProfile(firebaseUser, {
        displayName: data.organizationName
      });

      // Use writeBatch for atomic org + user doc creation
      const batch = writeBatch(db);
      const orgRef = doc(collection(db, 'organizations'));
      batch.set(orgRef, {
        name: data.organizationName,
        email: data.email,
        facilityType: data.facilityType,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      batch.set(doc(db, 'users', firebaseUser.uid), {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        organizationId: orgRef.id,
        role: 'owner',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await batch.commit();

      return {
        user: {
          id: firebaseUser.uid,
          email: firebaseUser.email || '',
          organizationId: orgRef.id,
          role: 'owner',
          createdAt: new Date(),
        } as User,
        organization: {
          id: orgRef.id,
          name: data.organizationName,
          email: data.email,
          facilityType: data.facilityType,
          createdAt: new Date(),
        } as Organization,
        token: await firebaseUser.getIdToken(),
      };
    } catch (error: any) {
      // If Firestore batch failed but Auth user was created, clean up
      if (userCredential?.user) {
        await userCredential.user.delete().catch(() => {});
      }
      throw new Error(error.message || 'Registration failed');
    }
  },

  async logout() {
    await signOut(auth);
  },

  async getCurrentUser() {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('Not authenticated');
      }

      // Prefer direct document read to satisfy typical Firestore rules
      let userData;
      const directUserDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      if (directUserDoc.exists()) {
        userData = directUserDoc.data();
      } else {
        // Fallback: try a constrained query only if necessary (may be blocked by rules)
        try {
          const q = query(collection(db, 'users'), where('uid', '==', firebaseUser.uid));
          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            userData = querySnapshot.docs[0].data();
          }
        } catch (permErr) {
          console.warn('[getCurrentUser] Query fallback blocked by rules:', permErr);
        }

        if (!userData) {
          console.warn('[getCurrentUser] User profile not found, creating basic profile');
          // Create a basic user profile with fallback data
          userData = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            organizationId: firebaseUser.uid, // Fallback to user ID as org ID
            displayName: firebaseUser.displayName || firebaseUser.email,
            createdAt: new Date(),
          };
        }
      }

      // Try to get organization data with fallback
      let orgData;
      try {
        const orgDoc = await getDoc(doc(db, 'organizations', userData.organizationId));
        if (orgDoc.exists()) {
          orgData = orgDoc.data();
        } else {
          console.warn('[getCurrentUser] Organization not found, creating fallback org data');
          orgData = {
            id: userData.organizationId,
            name: userData.displayName || userData.email || 'Organization',
            type: 'facility',
            createdAt: new Date(),
          };
        }
      } catch (orgError) {
        console.error('[getCurrentUser] Error fetching organization:', orgError);
        orgData = {
          id: userData.organizationId,
          name: userData.displayName || userData.email || 'Organization',
          type: 'facility',
          createdAt: new Date(),
        };
      }
    
      return {
        user: {
          id: firebaseUser.uid,
          email: firebaseUser.email || '',
          emailVerified: firebaseUser.emailVerified,
          organizationId: userData.organizationId,
          role: userData.role || 'admin',
          createdAt: userData.createdAt?.toDate() || new Date(),
        } as User,
        organization: {
          id: userData.organizationId,
          name: orgData?.name || userData.displayName || userData.email || 'Organization',
          email: orgData?.email || userData.email || '',
          facilityType: orgData?.facilityType || 'riding_school',
          subscription: orgData?.subscription,
          createdAt: orgData?.createdAt?.toDate() || new Date(),
        } as Organization,
        token: await firebaseUser.getIdToken(),
      };
    } catch (error: any) {
      console.error('[getCurrentUser] Error:', error);
      console.error('[getCurrentUser] Error details:', error.message, error.stack);
      throw error; // This one needs to throw since auth provider depends on it
    }
  },

  async sendEmailVerification(email?: string) {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('Not authenticated');
      }

      await sendEmailVerification(firebaseUser);
      return { success: true, message: 'Verification email sent' };
    } catch (error: any) {
      console.error('sendEmailVerification error:', error);
      throw new Error(error.message || 'Failed to send verification email');
    }
  },

  async checkEmailVerification() {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('Not authenticated');
      }

      // Reload user to get latest verification status
      await reload(firebaseUser);
      
      return {
        emailVerified: firebaseUser.emailVerified,
        email: firebaseUser.email
      };
    } catch (error: any) {
      console.error('checkEmailVerification error:', error);
      throw new Error(error.message || 'Failed to check verification status');
    }
  },

  async sendPasswordReset(email: string) {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true, message: 'Password reset email sent' };
    } catch (error: any) {
      console.error('sendPasswordReset error:', error);

      // Provide user-friendly error messages
      let errorMessage = 'Failed to send password reset email';

      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many requests. Please try again later';
      }

      throw new Error(errorMessage);
    }
  }
};

// Utility function to generate unique share links
const generatePublicLink = (title: string, customUrlEnding?: string): string => {
  // If custom URL ending is provided and valid, use it
  if (customUrlEnding && customUrlEnding.trim()) {
    return customUrlEnding.trim();
  }

  // Otherwise, generate from title
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();

  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substr(2, 5);

  return `${slug}-${timestamp}${random}`;
};

// Forms Functions
export const firebaseForms = {
  async createForm(formData: Partial<Form>) {
    try {
      const user = await getCurrentAuthUser();
      const organizationId = await getUserOrganizationId(user.uid);

      // Generate unique public link
      const publicLink = generatePublicLink(formData.title || 'form', formData.settings?.customUrlEnding);

      // Ensure default email settings if not provided
      const defaultEmailSettings = {
        enabled: true,
        sendToParticipant: true,
        sendToOrganization: false,
        organizationEmail: '',
        participantSubjectLine: '',
        organizationSubjectLine: '',
        includeFormResults: true,
        customParticipantMessage: '',
        customOrganizationMessage: ''
      };

      const docRef = await addDoc(collection(db, 'forms'), {
        ...formData,
        emailSettings: {
          ...defaultEmailSettings,
          ...(formData.emailSettings || {})
        },
        publicLink,
        organizationId,
        createdBy: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: formData.isActive ?? false, // Respect passed value, default to false
      });

      return {
        id: docRef.id,
        ...formData,
        publicLink,
        organizationId,
        createdBy: user.uid,
        createdAt: new Date(),
        updatedAt: new Date(),
        isActive: formData.isActive ?? false,
      } as Form;
    } catch (error: any) {
      console.error('createForm error:', error);
      throw error;
    }
  },

  async updateForm(formId: string, formData: Partial<Form>) {
    try {
      const user = await getCurrentAuthUser();

      // First, get the current form to check if URL ending has changed
      const currentFormDoc = await getDoc(doc(db, 'forms', formId));
      if (!currentFormDoc.exists()) {
        throw new Error('Form not found');
      }

      const currentData = currentFormDoc.data();
      const currentCustomUrlEnding = currentData.settings?.customUrlEnding;
      const newCustomUrlEnding = formData.settings?.customUrlEnding;

      // Prepare update data
      const updateData: any = {
        ...formData,
        updatedAt: serverTimestamp(),
      };

      // If custom URL ending has changed, update the publicLink
      if (newCustomUrlEnding !== currentCustomUrlEnding) {
        updateData.publicLink = generatePublicLink(
          formData.title || currentData.title || 'form',
          newCustomUrlEnding
        );
      }

      await updateDoc(doc(db, 'forms', formId), updateData);

      // Fetch the complete updated form to return all fields including publicLink
      const updatedFormDoc = await getDoc(doc(db, 'forms', formId));
      if (!updatedFormDoc.exists()) {
        throw new Error('Form not found after update');
      }

      const data = updatedFormDoc.data();
      return {
        id: updatedFormDoc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as Form;
    } catch (error: any) {
      console.error('updateForm error:', error);
      throw error;
    }
  },

  async getForm(formId: string) {
    const user = await getCurrentAuthUser();
    const organizationId = await getUserOrganizationId(user.uid);

    const q = query(
      collection(db, 'forms'),
      where(documentId(), '==', formId),
      where('organizationId', '==', organizationId)
    );
    const snap = await getDocs(q);

    if (snap.empty) {
      throw new Error('Form not found');
    }

    const formDoc = snap.docs[0];
    const data = formDoc.data();
    return {
      id: formDoc.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    } as Form;
  },

  async getForms() {
    try {
      // Check if we have a current user first
      let user;
      try {
        user = await getCurrentAuthUser();
      } catch (authError) {
        throw new Error('AUTH_REQUIRED');
      }

      let organizationId;
      try {
        organizationId = await getUserOrganizationId(user.uid);
      } catch (orgError) {
        console.error('[getForms] Organization ID error:', orgError);
        organizationId = user.uid; // Fallback to user ID
      }

      // Try to find forms with organization ID first
      let q = query(
        collection(db, 'forms'),
        where('organizationId', '==', organizationId),
        orderBy('updatedAt', 'desc')
      );

      let querySnapshot = await getDocs(q);

      // If no forms found and organizationId is different from user.uid, also try user.uid
      if (querySnapshot.docs.length === 0 && organizationId !== user.uid) {
        q = query(
          collection(db, 'forms'),
          where('organizationId', '==', user.uid),
          orderBy('updatedAt', 'desc')
        );
        querySnapshot = await getDocs(q);
      }
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      })) as Form[];
    } catch (error: any) {
      console.error('[getForms] Error:', error);
      console.error('[getForms] Error details:', error.message, error.stack);
      // Return empty array instead of throwing to prevent breaking the UI
      return [];
    }
  },

  async deleteForm(formId: string) {
    await getCurrentAuthUser(); // Ensure user is authenticated
    await deleteDoc(doc(db, 'forms', formId));
  },

  async getPublicForm(publicLink: string) {
    const q = query(
      collection(db, 'forms'),
      where('publicLink', '==', publicLink),
      where('isActive', '==', true)
    );

    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      throw new Error('Form not found or not active');
    }

    const formDoc = querySnapshot.docs[0];
    const data = formDoc.data();
    
    return {
      id: formDoc.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    } as Form;
  }
};

// Submissions Functions
export const firebaseSubmissions = {
  // Helper function to extract participant email from form data
  extractParticipantEmail(formData: Record<string, any>): string | null {
    // Look for email field in form data
    for (const [, value] of Object.entries(formData)) {
      if (typeof value === 'string' && value.includes('@') && value.includes('.')) {
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(value)) {
          return value;
        }
      }
    }
    return null;
  },

  // Helper function to extract participant name from form data
  extractParticipantName(formData: Record<string, any>): string {
    // First: prioritize fields with "name" in the key
    for (const [key, value] of Object.entries(formData)) {
      const keyLower = key.toLowerCase();
      if (keyLower.includes('name') && !keyLower.includes('email')) {
        // Handle compound name fields (e.g., { firstName, lastName })
        if (typeof value === 'object' && value !== null) {
          const parts = [value.firstName, value.lastName].filter(Boolean);
          if (parts.length > 0) return parts.join(' ');
        }
        if (typeof value === 'string' && value.trim()) {
          return value.trim();
        }
      }
    }
    // Fallback: first string that looks like a name
    for (const [, value] of Object.entries(formData)) {
      if (typeof value === 'string' && value.trim() &&
          (value.includes(' ') || value.length > 2)) {
        return value;
      }
    }
    return 'Participant';
  },

  async createSubmission(formId: string, submissionData: any) {
    try {
      // Use organizationId from submission data if available (public submissions),
      // otherwise fetch via authenticated getForm
      let organizationId = submissionData.organizationId;
      if (!organizationId) {
        const form = await firebaseForms.getForm(formId);
        organizationId = form.organizationId;
      }
      
      const ipAddress = await getClientIP().catch(() => 'unknown');
      
      // Prepare submission document - exclude undefined values
      const submissionDoc: any = {
        formId,
        formData: submissionData,
        submittedAt: serverTimestamp(),
        ipAddress: getPrivacyCompliantValue(ipAddress, 'ip'),
        userAgent: getPrivacyCompliantValue(navigator.userAgent, 'userAgent'),
        status: 'pending',
        organizationId: organizationId, // Use the form's organization ID
      };
      
      // Only include auditTrailId if it's not undefined
      if (submissionData.auditTrailId !== undefined && submissionData.auditTrailId !== null) {
        submissionDoc.auditTrailId = submissionData.auditTrailId;
      }

      const docRef = await addDoc(collection(db, 'submissions'), submissionDoc);

      // Email notifications will be handled by backend Firestore trigger
      // Email notifications handled by backend Firestore trigger

      return {
        id: docRef.id,
        formId,
        organizationId: organizationId,
        formData: submissionData,
        ipAddress: '',
        submittedAt: new Date(),
        status: 'pending' as const,
        auditTrailId: submissionData.auditTrailId,
      } as Submission;
    } catch (error) {
      console.error('Error creating submission:', error);
      throw error;
    }
  },

  async getSubmissions(formId?: string) {
    try {
      // Check if we have a current user first
      let user;
      try {
        user = await getCurrentAuthUser();
      } catch (authError) {
        console.error('[getSubmissions] Authentication error:', authError);
        return []; // Return empty array instead of throwing
      }

      let organizationId;
      try {
        organizationId = await getUserOrganizationId(user.uid);
      } catch (orgError) {
        console.error('[getSubmissions] Organization ID error:', orgError);
        organizationId = user.uid; // Fallback to user ID
      }

      let submissions = [];

      if (formId) {
        // Query by specific form AND organization to match security rules
        const q = query(
          collection(db, 'submissions'),
          where('formId', '==', formId),
          where('organizationId', '==', organizationId),
          orderBy('submittedAt', 'desc')
        );
        const querySnapshot = await getDocs(q);
        submissions = querySnapshot.docs;
      } else {
        // Query submissions by organization ID with dual-query pattern for backward compatibility
        try {
          const q = query(
            collection(db, 'submissions'),
            where('organizationId', '==', organizationId),
            orderBy('submittedAt', 'desc')
          );
          const querySnapshot = await getDocs(q);
          submissions = querySnapshot.docs;

          // If no submissions found and organizationId != userId, try with userId as organizationId (backward compatibility)
          if (submissions.length === 0 && organizationId !== user.uid) {
            const fallbackQuery = query(
              collection(db, 'submissions'),
              where('organizationId', '==', user.uid),
              orderBy('submittedAt', 'desc')
            );
            const fallbackSnapshot = await getDocs(fallbackQuery);
            submissions = fallbackSnapshot.docs;
          }
        } catch (indexError: any) {
          // SECURITY: DO NOT fall back to downloading all submissions - this violates data isolation
          console.error('[getSubmissions] Query failed:', indexError.message);
          submissions = [];
        }
      }

      return submissions.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          formId: data.formId,
          organizationId: data.organizationId || organizationId,
          formData: data.formData || data.data || {},
          status: data.status || 'pending',
          submittedAt: data.submittedAt?.toDate() || new Date(),
          reviewedAt: data.reviewedAt?.toDate(),
          reviewedBy: data.reviewedBy,
          ipAddress: data.ipAddress || '',
          notes: data.notes,
          auditTrailId: data.auditTrailId,
        } as Submission;
      });
    } catch (error: any) {
      console.error('getSubmissions error:', error);
      console.error('getSubmissions error details:', error.message, error.stack);
      
      // For permissions errors, throw the error to prevent infinite retries
      if (error.message && error.message.includes('permissions')) {
        throw error;
      }
      
      // For other errors, return empty array to prevent breaking the UI
      return [];
    }
  },

  async updateSubmissionStatus(submissionId: string, status: string, notes?: string) {
    try {
      const user = await getCurrentAuthUser();

      await updateDoc(doc(db, 'submissions', submissionId), {
        status,
        notes,
        reviewedAt: serverTimestamp(),
        reviewedBy: user.uid,
      });
    } catch (error: any) {
      console.error('updateSubmissionStatus error:', error);
      throw error;
    }
  },

  async getForms() {
    return firebaseForms.getForms();
  },

  async deleteSubmission(submissionId: string) {
    await getCurrentAuthUser();
    await deleteDoc(doc(db, 'submissions', submissionId));
  },

  async deleteSubmissions(submissionIds: string[]) {
    await getCurrentAuthUser();
    if (submissionIds.length === 1) {
      await this.deleteSubmission(submissionIds[0]);
      return;
    }
    const batch = writeBatch(db);
    for (const id of submissionIds) {
      batch.delete(doc(db, 'submissions', id));
    }
    await batch.commit();
  }
};

// Google Drive integration methods
const firebaseGoogleDrive = {
  async updateGoogleDriveConnection(connectionData: {
    connected: boolean;
    userEmail: string;
    connectedAt: string | null;
  }) {
    try {
      const user = await getCurrentAuthUser();
      const org = await this.getCurrentUser();
      
      if (!org?.organization?.id) {
        throw new Error('Organization not found');
      }

      await setDoc(
        doc(db, 'organizations', org.organization.id, 'integrations', 'googledrive'),
        {
          ...connectionData,
          updatedAt: serverTimestamp(),
          updatedBy: user.uid
        },
        { merge: true }
      );
    } catch (error: any) {
      console.error('updateGoogleDriveConnection error:', error);
      throw error;
    }
  },

  async updateGoogleDriveSettings(settings: {
    autoBackupEnabled: boolean;
    backupPDFs: boolean;
    backupCSVs: boolean;
    folderName: string;
  }) {
    try {
      const user = await getCurrentAuthUser();
      const org = await this.getCurrentUser();
      
      if (!org?.organization?.id) {
        throw new Error('Organization not found');
      }

      await setDoc(
        doc(db, 'organizations', org.organization.id, 'settings', 'googledrive'),
        {
          ...settings,
          updatedAt: serverTimestamp(),
          updatedBy: user.uid
        },
        { merge: true }
      );
    } catch (error: any) {
      console.error('updateGoogleDriveSettings error:', error);
      throw error;
    }
  },

  async getGoogleDriveConnection() {
    try {
      const org = await this.getCurrentUser();
      
      if (!org?.organization?.id) {
        return null;
      }

      const connectionDoc = await getDoc(
        doc(db, 'organizations', org.organization.id, 'integrations', 'googledrive')
      );

      return connectionDoc.exists() ? connectionDoc.data() : null;
    } catch (error: any) {
      console.error('getGoogleDriveConnection error:', error);
      return null;
    }
  },

  async getGoogleDriveSettings() {
    try {
      const org = await this.getCurrentUser();
      
      if (!org?.organization?.id) {
        return null;
      }

      const settingsDoc = await getDoc(
        doc(db, 'organizations', org.organization.id, 'settings', 'googledrive')
      );

      return settingsDoc.exists() ? settingsDoc.data() : null;
    } catch (error: any) {
      console.error('getGoogleDriveSettings error:', error);
      return null;
    }
  }
};

// Export combined API client compatible with existing code
export const firebaseApiClient = {
  ...firebaseAuth,
  ...firebaseForms,
  ...firebaseSubmissions,
  ...firebaseGoogleDrive,
};